/**
 * HitlSettings (I6 · Figma 129:519) — instructor HITL preferences.
 *
 * Wave 2 knobs surfaced:
 *   - wait_threshold_min (1-120)      "page me if a queue item waits > N min"
 *   - max_concurrent (1-20)           "refuse new takes if I'm already in N"
 *   - hours_start / hours_end (HH:MM) optional office hours
 *   - timezone (text; not validated) — Wave 3 will add IANA picker
 *   - notify_when_ready (bool)        "email/push me on assignment"
 *
 * The read-through auto-creates the row so a fresh instructor sees defaults
 * immediately. Save is optimistic: PATCH the changed keys, refresh on 200.
 */

import React, { useEffect, useState } from 'react';
import { hitlApi, InstructorPreferences } from '../../services/hitlApi';
import { styles } from './HitlStyles';

const HitlSettings: React.FC = () => {
  const [prefs, setPrefs] = useState<InstructorPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await hitlApi.getPreferences();
        if (!cancelled) setPrefs(p);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = <K extends keyof InstructorPreferences>(k: K, v: InstructorPreferences[K]) => {
    setPrefs((prev) => (prev ? { ...prev, [k]: v } : prev));
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    setError(null);
    try {
      const p = await hitlApi.updatePreferences({
        wait_threshold_min: prefs.wait_threshold_min,
        max_concurrent: prefs.max_concurrent,
        hours_start: prefs.hours_start,
        hours_end: prefs.hours_end,
        timezone: prefs.timezone,
        notify_when_ready: prefs.notify_when_ready,
      });
      setPrefs(p);
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs && !error) return <div style={styles.page}>Loading preferences…</div>;
  if (error && !prefs) {
    return (
      <div style={styles.page}>
        <div
          role="alert"
          style={{ ...styles.card, background: '#FEF2F2', borderColor: '#FCA5A5', color: '#B91C1C' }}
        >
          {error}
        </div>
      </div>
    );
  }
  if (!prefs) return null;

  return (
    <div style={{ ...styles.page, maxWidth: 640 }}>
      <h1 style={styles.h1}>HITL preferences</h1>
      <p style={styles.subtitle}>
        Tune how the queue routes escalations to you.
      </p>

      <div style={styles.card}>
        <label>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Wait threshold (minutes)</div>
          <div style={{ ...styles.muted, marginBottom: 8 }}>
            Page me when an escalation has been waiting longer than this.
          </div>
          <input
            type="number"
            min={1}
            max={120}
            value={prefs.wait_threshold_min}
            onChange={(e) => update('wait_threshold_min', Number(e.target.value))}
            style={{ ...styles.input, width: 120 }}
          />
        </label>
      </div>

      <div style={styles.card}>
        <label>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Max concurrent sessions</div>
          <div style={{ ...styles.muted, marginBottom: 8 }}>
            The queue will refuse a "Take" once you have this many active sessions.
          </div>
          <input
            type="number"
            min={1}
            max={20}
            value={prefs.max_concurrent}
            onChange={(e) => update('max_concurrent', Number(e.target.value))}
            style={{ ...styles.input, width: 120 }}
          />
        </label>
      </div>

      <div style={styles.card}>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>Working hours (optional)</div>
        <div style={{ ...styles.muted, marginBottom: 8 }}>
          Leave blank to be considered always-available.
        </div>
        <div style={styles.row}>
          <input
            type="time"
            value={prefs.hours_start ?? ''}
            onChange={(e) => update('hours_start', e.target.value || null)}
            style={{ ...styles.input, width: 150 }}
          />
          <span>–</span>
          <input
            type="time"
            value={prefs.hours_end ?? ''}
            onChange={(e) => update('hours_end', e.target.value || null)}
            style={{ ...styles.input, width: 150 }}
          />
          <input
            placeholder="Timezone (e.g. Pacific/Auckland)"
            value={prefs.timezone}
            onChange={(e) => update('timezone', e.target.value)}
            style={{ ...styles.input, flex: 1 }}
          />
        </div>
      </div>

      <div style={styles.card}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={prefs.notify_when_ready}
            onChange={(e) => update('notify_when_ready', e.target.checked)}
          />
          <div>
            <div style={{ fontWeight: 500 }}>Notify me when an escalation is assigned</div>
            <div style={styles.muted}>Push + email. Uncheck to page manually only.</div>
          </div>
        </label>
      </div>

      {error && (
        <div
          role="alert"
          style={{ ...styles.card, background: '#FEF2F2', borderColor: '#FCA5A5', color: '#B91C1C' }}
        >
          {error}
        </div>
      )}

      {savedAt && !error && (
        <div style={{ ...styles.muted, marginBottom: 12 }}>
          Saved at {savedAt.toLocaleTimeString()}
        </div>
      )}

      <button
        disabled={saving}
        style={{ ...styles.button, ...styles.primary, opacity: saving ? 0.6 : 1 }}
        onClick={save}
      >
        {saving ? 'Saving…' : 'Save preferences'}
      </button>
    </div>
  );
};

export default HitlSettings;
